/**
 * utils/lazyRetry.js — Chunk ачаалалтыг ЗАСВАРЛАХ чадвартай `React.lazy`.
 *
 * АСУУДАЛ ЮУНААС ГАРАВ:
 *   Code splitting хийсний дараа browser нь `static/js/473.abc.chunk.js` гэх
 *   мэт файлыг ТУСДАА хүсэлтээр татдаг болсон. Харин nginx-ийн
 *   `try_files $uri /index.html` нь ОЛДООГҮЙ ямар ч файлд index.html-ийг
 *   200 статустай буцаадаг — .js файлд ч мөн адил. Тэгэхээр:
 *
 *     1) chunk алга  →  nginx HTML-ийг JS нэрээр өгнө (404 биш!)
 *     2) browser HTML-ийг JS гэж уншина → SyntaxError
 *     3) import() reject → React.lazy throw
 *     4) ErrorBoundary байхгүй → React БҮХ модыг unmount → ЦАГААН ХУУДАС
 *
 *   Chunk хэзээ алга болдог вэ: deploy хийж байх агшинд хуудас нээсэн,
 *   эсвэл буцаалт (rollback) хийхэд браузерт шинэ index.html үлдсэн үед.
 *
 * ШИЙДЭЛ:
 *   Алдаа гармагц шууд бууж өгөхгүй — 3 шатлалтай сэргээлт:
 *     1-р шат: 400ms хүлээгээд ДАХИН оролдоно (түр зуурын сүлжээний саатал)
 *     2-р шат: сессэд нэг удаа хуудсыг автоматаар reload хийнэ. index.html
 *              нь `no-store` тул ШИНЭ hash-тай ирнэ → chunk олдоно.
 *     3-р шат: reload ч тусалсангүй (deploy үнэхээр дутуу) → алдааг цааш
 *              шидэж, ChunkErrorBoundary уншигдах мэдэгдэл харуулна.
 *
 *   Мөн TIMEOUT нэмсэн: хүсэлт хариу өгөхгүй ӨЛГӨГДВӨЛ Suspense мөнхөд
 *   эргэлдэхийн оронд алдаа болж, дээрх сэргээлт ажиллана. Энэ нь
 *   «гацалт»-ыг шийддэг хэсэг.
 */

import { lazy } from "react";

/** Chunk татахыг хэдэн секунд хүлээх вэ. Дараа нь «гацсан» гэж үзнэ. */
const TIMEOUT_MS = 20 * 1000;

/** Эхний бүтэлгүйтлийн дараа дахин оролдохын өмнөх завсарлага. */
const RETRY_DELAY_MS = 400;

/** Автомат reload-ыг давтахгүйн тулд тэмдэглэх түлхүүр. */
const RELOAD_KEY = "hse_chunk_reload_at";

/** Энэ хугацаанд дахин reload хийхгүй — давталтын гогцоонд орохоос сэргийлнэ. */
const RELOAD_COOLDOWN_MS = 30 * 1000;

/**
 * Алдаа нь chunk ачаалалтынх эсэхийг таних.
 *
 * Энгийн програмын алдаанаас ЗААВАЛ ялгах ёстой: chunk алдаанд reload
 * тусална, харин жинхэнэ bug-д reload нь зөвхөн хэрэглэгчийн ажлыг устгана.
 */
export function isChunkError(error) {
  if (!error) return false;
  const name = String(error.name || "");
  const msg = String(error.message || error);
  return (
    // webpack 5 — стандарт нэр
    name === "ChunkLoadError" ||
    /Loading chunk \S+ failed/i.test(msg) ||
    /Loading CSS chunk \S+ failed/i.test(msg) ||
    // HTML-ийг JS болгон уншсан үед (яг манай nginx-ийн тохиолдол)
    /Unexpected token '?</i.test(msg) ||
    // Vite/native ESM хэлбэрүүд — ирээдүйд шилжвэл ажиллана
    /Failed to fetch dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    // өөрсдийн timeout
    name === "ChunkTimeoutError"
  );
}

/**
 * Сессэд нэг удаа хуудсыг шинэчлэх.
 * @returns {boolean} reload эхэлсэн эсэх. false бол аль хэдийн оролдсон.
 */
export function reloadOnce() {
  let last = 0;
  try {
    last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
  } catch {
    // sessionStorage хаалттай (private горим) — тэмдэглэл хийж чадахгүй тул
    // давталтаас сэргийлэх боломжгүй. Аюулгүй тал руу — reload хийхгүй.
    return false;
  }
  if (last && Date.now() - last < RELOAD_COOLDOWN_MS) return false;
  try {
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    return false;
  }
  window.location.reload();
  return true;
}

/** Амжилттай ачаалсны дараа тэмдэглэлийг цэвэрлэнэ. */
export function clearReloadMark() {
  try {
    window.sessionStorage.removeItem(RELOAD_KEY);
  } catch {
    /* хамаагүй */
  }
}

function withTimeout(promise, ms) {
  return new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error(`Chunk ачаалалт ${ms}ms-д багтсангүй`);
      err.name = "ChunkTimeoutError";
      reject(err);
    }, ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      },
    );
  });
}

const wait = (ms) => new Promise((r) => setTimeout(r, ms));

/**
 * `lazy()`-ийн оронд ашиглана — API нь яг ижил.
 *
 *   const RiskRoute = lazyRetry(() => import("src/routers/risk"));
 *
 * @param {() => Promise<any>} factory  `() => import(...)`
 */
export function lazyRetry(factory) {
  return lazy(async () => {
    try {
      const mod = await withTimeout(factory(), TIMEOUT_MS);
      clearReloadMark();
      return mod;
    } catch (first) {
      if (!isChunkError(first)) throw first;

      // 1-р шат — түр зуурын саатал байж магадгүй, дахин оролдоно.
      try {
        await wait(RETRY_DELAY_MS);
        const mod = await withTimeout(factory(), TIMEOUT_MS);
        clearReloadMark();
        return mod;
      } catch (second) {
        if (!isChunkError(second)) throw second;

        // 2-р шат — index.html хуучирсан байх магадлалтай. Шинэчилнэ.
        if (reloadOnce()) {
          // Хуудас дахин ачаалагдаж эхэлсэн. Энэ хооронд алдааны дэлгэц
          // анивчуулахгүйн тулд хэзээ ч дуусахгүй promise буцаана.
          return new Promise(() => {});
        }

        // 3-р шат — reload аль хэдийн оролдсон. ErrorBoundary шийднэ.
        throw second;
      }
    }
  });
}

export default lazyRetry;
